import React from "react";
import Modal from "react-modal";
import _ from "lodash";
import Image from "next/image";
import Link from "next/link";
import moment from "moment";
import TippyIcon from "../TippyIcon";

const modalStyles = {
	content: {
		top: "50%",
		left: "50%",
		right: "auto",
		bottom: "auto",
		marginRight: "-40%",
		transform: "translate(-50%, -50%)",
		minwidth: "320px",
		maxWidth: "720px",
		minheight: "32px",
		maxheight: "80vh"
	}
};

const PaymentErrorModal = ({ isOpen, closeModal, errorType, onClickBtn }) => {
	const allErrors = {
		/* Select Another ticket error */

		"ASC-3":
			"The ticket for which you tried to register has expired and is not available anymore.", //1
		"ASC-4": "The ticket for which you tried to register is yet to open for sales", //1
		"ASC-5":
			"The ticket for which you tried to register has unfortunately been sold out by the time you completed your registration.", //1
		"ASC-20": "Ticket is not accessible.", //1
		"ASC-21": "The ticket you tried to purchase has been deleted by the organiser.", //1
		"ASC-27": " Ticket is not accepting registrations",

		/* Transaction Failed error */

		"ASC-14": "All tickets in a transaction should belong to the same currency", //2
		"ASC-19": "Selected payment method is not allowed for this event", //2
		"ASC-15": "There was a change in the ticket price that you tried to register for.", //2
		"ASC-22":
			"The number of registrations in the particular transaction is less than the configured min team member size.", //2

		/* Registration Failed error */

		"ASC-2":
			"The event is no longer accepting registrations and may have been deleted by the organiser.", //3
		"ASC-6":
			"The number of tickets selected in the particular registration is greater than the configured number.", //3
		"ASC-7": "Ticket sales exceeds number of tickets available", //3
		"ASC-8": "The code used for the registration is invalid.", //3
		"ASC-9": "The coupon used for the transaction has unfortunately expired.", //3
		"ASC-10": "The coupon used for the transaction is invalid.", //3
		"ASC-11":
			"The coupon used for the transaction is not applicable for any of the selected tickets.", //3
		"ASC-12":
			"The coupon used for the transaction has unfortunately exhausted and can not be used anymore.", //3
		"ASC-13": "Multiple donation tickets cannot be purchased in a single transaction.", //3
		"ASC-16": "The donation amount doesn't meet minimum donation amount for the ticket.", //3
		"ASC-17": "The donation amount exceeds the maximum value configured for the ticket.", //3
		"ASC-18": "The OTP entered at the time of registration is invalid or expired.", //3
		"LD-4": "Invalid payment method selected. Please choose another payment method.", //3
		"LD-5": "Registration for paid tickets will be available once the payment method is set up by the organizer.", //3
		"LD-6": "TAP account is not connected for this event.",
		"LD-7": "Error while creating order,Please try again later or contact the event organizer.",
		"CPTR-4": "Oops! Looks like you have already registered for the event!",

		/*max ticket selection exceeds*/
		"ASC-26":
			"Your ticket selection exceeds the remaining available limit. Please reduce the number of tickets to select more.",
		"ASC-24": "Applied coupon is not applicable for the selected no.of ticket in a transaction",

		/* Unexpected Error */

		"LD-1": "Order amount must be greater than or equal to {amount} {currency}", //4
		"ASC-1": "The total transaction amount should be more than 0.", //4
		"LD-2": "Order Amount must be greater than or equal to 1", //4
		"LD-3": "Mandatory field", //4

		"CPTR-1": "Missing mandatory fields",
		"CPTR-2": "Invalid Payment",
		"CPTR-3": "Unable to process the payment",

		"PGW-1": "The payment gateway failed to process the payment. Please try again."
	};

	const categorizedErrorCodes = {
		"Registration Failed": [
			"ASC-2",
			"ASC-6",
			"ASC-7",
			"ASC-8",
			"ASC-9",
			"ASC-10",
			"ASC-11",
			"ASC-12",
			"ASC-13",
			"ASC-16",
			"ASC-17",
			"ASC-18",
			"LD-4",
			"LD-5",
			"LD-6",
			"LD-7",
			"CPTR-4"
		],
		"Transaction Failed": ["ASC-14", "ASC-19", "ASC-15", "ASC-22"],
		"Unexpected Error": [
			"LD-1",
			"ASC-1",
			"LD-2",
			"LD-3",
			"CPTR-1",
			"CPTR-2",
			"CPTR-3",
			"PGW-1"
		],
		"Tickets Selection": ["ASC-26", "ASC-24"],
		"Select Another Error": ["ASC-3", "ASC-4", "ASC-5", "ASC-20", "ASC-21", "ASC-27"]
	};

	const hasButtons =
		categorizedErrorCodes["Select Another Error"].includes(errorType) ||
			categorizedErrorCodes["Transaction Failed"].includes(errorType)
			? true
			: false;

	const displayErrorHeading = errorType => {
		if (errorType === "unexpected") {
			return "Unexpected Error";
		} else {
			const foundError = _.findKey(categorizedErrorCodes, function (o) {
				return o.includes(errorType);
			});
			return foundError === "Select Another Error" ? "Registration Failed" : foundError;
		}
	};

	const errorContent = () => {
		if (errorType !== "unexpected" || _.has(allErrors, errorType)) {
			return (
				<>
					<p className={`payment-error-body-message ${hasButtons ? "mb-4" : "mb-2"}`}>
						{allErrors[errorType]}
					</p>
				</>
			);
		} else {
			return (
				<>
					<p className="payment-error-body-message mb-4">
						Oops! Something went wrong. We apologize for the inconvenience. We're
						looking into the issue and will resolve it soon.
					</p>
					<p className="mt-3 payment-error-body-message">Please try again later.</p>
				</>
			);
		}
	};

	return (
		<>
			<Modal
				isOpen={isOpen}
				onRequestClose={() => closeModal()}
				shouldCloseOnEsc={false}
				shouldCloseOnOverlayClick={false}
				style={modalStyles}
			>
				<div className="row p-3 align-items-center d-flex">
					{!categorizedErrorCodes["Select Another Error"].includes(errorType) && (
						<button className="closeIcon-btn p-0" onClick={() => closeModal()}>
							<i className="fas fa-times closeIcon d-flex"></i>
						</button>
					)}
					<div className="col-5 align-items-center d-none d-md-flex justify-content-center">
						<img
							src={
								categorizedErrorCodes["Unexpected Error"].includes(errorType)
									? `/img/500.svg`
									: "/img/modal/octopus-err.svg"
							}
							className="error-main-img w-100"
						/>
					</div>
					<div className="col-7 justify-content-center content-wrapper">
						<h3 className="error-header mb-4">
							<img src="/img/modal/warning.svg" className="error-warning" />
							{displayErrorHeading(errorType)}
						</h3>

						{errorContent()}

						{hasButtons && (
							<>
								<button className="error-btn mb-4 px-4 py-2" onClick={onClickBtn}>
									{categorizedErrorCodes["Select Another Error"].includes(
										errorType
									)
										? "Select Another Ticket"
										: "Try Again"}
								</button>
								<p className="error-note">
									<strong>Note:</strong> If any money got deducted, it will be
									automatically refunded within 5-7 business days.
								</p>
							</>
						)}
					</div>
				</div>
			</Modal>
			<style jsx>{`
				.closeIcon-btn {
					border: none;
					outline: none;
					position: absolute;
					width: auto;
					right: 20px;
					top: 20px;
					background: none;
				}
				.payment-error-body-message {
					font-weight: 500;
					color: #572148;
				}
				.payment-error-color-change {
					color: #fb5850;
				}
				.closeIcon {
					font-size: 25px;
					color: rgba(0, 0, 0, 0.3);
				}
				.error-header {
					color: #dc3545;
					display: flex;
					align-items: center;
				}
				.error-warning {
					margin-right: 10px;
				}
				.error-btn {
					border: none;
					outline: none;
					background: #572148;
					color: white;
				}
				.error-note {
					color: #572148;
					font-size: 14px;
					font-weight: 500;
				}

				@media (max-width: 768px) {
					.content-wrapper {
						width: 100%;
					}
				}
			`}</style>
		</>
	);
};

export default PaymentErrorModal;
